/**
 * Pure helpers for the Funnels list (Paper → "Heatmaps, Funnels + Attribution"
 * → "Funnels · List · 1440", "Funnels · List · Loading", "Funnels · None saved").
 *
 * The view itself is `FunnelLibraryView`; FunnelStudio feeds it real data.
 */
import type { FunnelRequestParams, StepCondition } from "@/lib/types/funnel-api"

export type FunnelChipType = "page_view" | "click" | "form_submission"

export type FunnelChip = { type: FunnelChipType; label: string }

/** Per-funnel result for the selected period. Null fields render "–". */
export type FunnelRowResult = {
  entered: number
  completed: number
  /** Percent, 0–100 */
  conversion: number
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const toDate = (v: Date | string) => (v instanceof Date ? v : new Date(v))

/** "Sep 24" */
export function formatShortDate(value: Date | string | null | undefined): string | null {
  if (!value) return null
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return null
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`
}

/**
 * Inclusive range label. `compact` drops the repeated month on the end date,
 * the way the previous-period caption reads ("Sep 12 to 18"); the page
 * subtitle keeps both months ("Sep 19 to Sep 25").
 */
export function formatRangeLabel(
  start: Date | string,
  end: Date | string,
  { compact = false }: { compact?: boolean } = {},
): string | null {
  const s = toDate(start)
  const e = toDate(end)
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null
  const from = `${MONTHS[s.getMonth()]} ${s.getDate()}`
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()
  const to = compact && sameMonth ? String(e.getDate()) : `${MONTHS[e.getMonth()]} ${e.getDate()}`
  return `${from} to ${to}`
}

/** Saved funnels come back in a couple of API shapes. */
export function savedFunnelId(funnel: any): string | undefined {
  return funnel?.funnel_id || funnel?.funnelId || funnel?._id || funnel?.id
}

export function savedFunnelUpdatedAt(funnel: any): string | null {
  return funnel?.updated_at || funnel?.updatedAt || funnel?.created_at || funnel?.createdAt || null
}

/** Step chips: page steps show their path, click and form steps their name. */
export function funnelChips(funnel: any): FunnelChip[] {
  const steps: any[] = Array.isArray(funnel?.steps) ? funnel.steps : []
  return steps.map((s) => {
    const type: FunnelChipType =
      s?.type === "click" || s?.type === "form_submission" ? s.type : "page_view"
    const path = s?.url || s?.path || ""
    const label = type === "page_view" ? path || s?.name || "" : s?.name || path
    return { type, label }
  })
}

type FilterFields = Partial<
  Pick<
    StepCondition,
    | "utm_source" | "utm_medium" | "utm_campaign" | "utm_content" | "utm_id" | "utm_term"
    | "device" | "os" | "browser" | "referrer" | "region" | "country"
  >
>

/**
 * Request params for one saved funnel over a window, built the same way
 * FunnelStudio builds them for a loaded funnel (sequential mode, filter chips
 * AND-merged into every condition). Null when fewer than two steps have a
 * target, which the API rejects.
 */
export function savedFunnelParams(
  funnel: any,
  window: { start: string; end: string; timezone: string; granularity: string },
  propertyId: string | undefined,
  filterFields: FilterFields = {},
): FunnelRequestParams | null {
  const steps: any[] = (Array.isArray(funnel?.steps) ? funnel.steps : []).filter(
    (s: any) => String(s?.url || s?.path || "").trim(),
  )
  if (steps.length < 2) return null
  return {
    start: window.start,
    end: window.end,
    mode: "sequential",
    timezone: window.timezone,
    granularity: window.granularity,
    steps: steps.map((s: any, i: number) => {
      const target = String(s.url || s.path)
      const extra: Array<{ value: string; secondary?: string }> = s.additionalTargets || []
      const stepPropertyId = s.propertyId && s.propertyId !== propertyId ? s.propertyId : undefined
      const conditions: StepCondition[] =
        !s.type || s.type === "page_view"
          ? [
              { type: "page_view", path: target, ...filterFields },
              ...extra.map((t) => ({ type: "page_view" as const, path: t.value, ...filterFields })),
            ]
          : [
              { type: "event", action: s.type, target, ...(s.secondary && { secondary: s.secondary }), ...filterFields },
              ...extra.map((t) => ({
                type: "event" as const,
                action: s.type,
                target: t.value,
                ...(t.secondary && { secondary: t.secondary }),
                ...filterFields,
              })),
            ]
      return {
        name: s.name || `Step ${i + 1}`,
        ...(stepPropertyId && { property_id: stepPropertyId }),
        conditions,
      }
    }),
  }
}

export function summarizeFunnelResult(
  summary: { total_entered?: number; total_completed?: number } | null | undefined,
): FunnelRowResult | null {
  if (!summary) return null
  const entered = Number(summary.total_entered) || 0
  const completed = Number(summary.total_completed) || 0
  return { entered, completed, conversion: entered > 0 ? (completed / entered) * 100 : 0 }
}

export function formatConversion(pct: number): string {
  return `${pct.toFixed(1)}%`
}

/**
 * The page answer: the lowest-converting funnel with traffic. When it is also
 * the busiest funnel, say so, since that is where a fix pays most. Null when
 * no funnel has results yet.
 */
export function pickFunnelAnswer(
  rows: Array<{ id: string; name: string; result: FunnelRowResult | null }>,
): { id: string; headline: string } | null {
  const withTraffic = rows.filter((r) => r.result && r.result.entered > 0)
  if (withTraffic.length === 0) return null
  const lowest = withTraffic.reduce((a, b) => (b.result!.conversion < a.result!.conversion ? b : a))
  const busiest = withTraffic.reduce((a, b) => (b.result!.entered > a.result!.entered ? b : a))
  const rate = formatConversion(lowest.result!.conversion)
  const headline =
    withTraffic.length > 1 && busiest.id === lowest.id
      ? `${lowest.name} gets the most visitors and converts lowest, at ${rate}.`
      : withTraffic.length > 1
        ? `${lowest.name} converts lowest, at ${rate}.`
        : `${lowest.name} converts at ${rate}.`
  return { id: lowest.id, headline }
}

/** "+15%" / "−4%" / "0%" for the visitors tile. */
export function formatSignedPercent(delta: number): string {
  if (delta > 0) return `+${delta}%`
  if (delta < 0) return `−${Math.abs(delta)}%`
  return "0%"
}
